import * as React from 'react';
import currency from 'currency.js';
import { connect } from 'react-redux';
import { get, isEmpty, isNil, mapValues, snakeCase } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';
import { ModalFrame, ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';
import Input from 'components/inputs/input/Input';
import { Form, FormField } from 'components/form';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import CreatorTilePriceReco from 'components/creator-tile/price-reco/CreatorTilePriceReco';

import { saveCurrentCustomizedFields } from '../../../reducers/bulkOffer';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';
import { fetchInfluncerMetrics } from 'modules/influencers/reducers/influencersMetrics';

import { __t, applyTranslations } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import {
  getBulkOfferSelectedSubcampaigns,
  isBulkOfferModalEditable,
  sumBulkOfferBudgets
} from 'utils/bulk-offer';
import {
  LONG_STRING_MAX_LENGTH,
  isIntegerMoneySum,
  isNotEmpty,
  isPositiveNumber,
  isRequired,
  maxLength
} from 'utils/validators';
import { sortParticipants } from 'utils/campaignParticipants';
import { isDefined } from 'utils/helper/common';

import { PromptExit } from 'libs/router';

import type {
  Campaign,
  CampaignOverview,
  InfluencerMetrics,
  Subcampaign,
  SubcampaignBulkOfferStore,
  SubcampaignCreateBulkOfferCustomField,
  SubcampaignCreateBulkOfferCustomizedFieldsMap,
  SubcampaignOfferForm,
  TextEntryKeyboardEvent
} from 'types';

import './bulk-customize-offer-modal.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./bulk-customize-offer-modal.i18n.json'));

type Handler = () => void;

type OwnProps = {
  fieldName: SubcampaignCreateBulkOfferCustomField,
  bulkOffer: SubcampaignOfferForm,
  onCloseModal: Handler,
  onChanged?: (a: SubcampaignCreateBulkOfferCustomField) => void
};

type StateProps = {
  bulkOfferStore: SubcampaignBulkOfferStore,
  campaignOverview: CampaignOverview | undefined | null,
  campaignBrief: Campaign | undefined | null,
  influencersMetrics: InfluencerMetrics
};

type DispatchProps = {
  dispatchSaveCurrentCustomizedFields: (a: SubcampaignCreateBulkOfferCustomizedFieldsMap) => Promise<void>,
  dispatchFetchInfluncerMetrics: (campaignId: number | undefined, influencerIds: Array<number>) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isModified: boolean,
  selectedSubcampaigns: Array<Subcampaign>,
  customizedFieldsByIdMap: SubcampaignCreateBulkOfferCustomizedFieldsMap,
  isEditMode: boolean
};

export class BulkCustomizeOfferModal extends React.PureComponent<Props, State> {
  formRef: React.RefObject<any> = React.createRef();

  constructor(props: Props) {
    super(props);

    const { fieldName, bulkOfferStore } = props;
    const selectedSubcampaigns = getBulkOfferSelectedSubcampaigns(bulkOfferStore);
    const fallbackValues = this.prefillMapWithFallbackValues(props, selectedSubcampaigns);

    this.state = {
      selectedSubcampaigns,
      customizedFieldsByIdMap: fallbackValues,
      isModified: false,
      isEditMode: isBulkOfferModalEditable(fallbackValues, fieldName)
    };
  }

  componentDidMount() {
    const { dispatchFetchInfluncerMetrics } = this.props;
    const { selectedSubcampaigns } = this.state;

    dispatchFetchInfluncerMetrics(undefined, selectedSubcampaigns.map(subcampaign => subcampaign.creatorProfile.id));
  }

  dispatchTrackEvent() {
    const { fieldName } = this.props;

    addUserAction({
      eventCategory: 'bulk-offer',
      eventAction: 'bulk-offer-customized',
      eventLabel: fieldName
    });
  }

  prefillMapWithFallbackValues(props: Props, selectedSubcampaigns: Array<Subcampaign>): SubcampaignCreateBulkOfferCustomizedFieldsMap {
    const { bulkOffer, bulkOfferStore, fieldName } = props;

    return selectedSubcampaigns.reduce((mapById, subcampaign) => {
      let fallbackValue = bulkOffer[fieldName] || null;
      if (!fallbackValue && (fieldName === 'legalRequirements' || fieldName === 'personalMessage')) {
        fallbackValue = '';
      }
      mapById[subcampaign.id] = { // eslint-disable-line no-param-reassign
        ...(bulkOfferStore.customizedFieldsByIdMap[subcampaign.id] || null),
        // $FlowIgnore - Cannot use fieldName [1] as a computed property. Computed properties may only be primitive literal values
        [fieldName]: get(bulkOfferStore, `customizedFieldsByIdMap.${subcampaign.id}.${fieldName}`) || fallbackValue
      };
      return mapById;
    }, {});
  }

  calcRemainingBudget(): number {
    const { bulkOffer, bulkOfferStore, campaignOverview } = this.props;
    const { customizedFieldsByIdMap } = this.state;

    const remainingBudgetBeforeBulkOffer = get(campaignOverview, 'remainingBudget') || 0;
    const sum = sumBulkOfferBudgets(
      getBulkOfferSelectedSubcampaigns(bulkOfferStore),
      customizedFieldsByIdMap,
      bulkOffer.budget
    );
    return currency(remainingBudgetBeforeBulkOffer).subtract(currency(sum)).value;
  }

  handleChanged = (subcampaignId: number, value: string | number | undefined | null): void => {
    const { fieldName } = this.props;

    this.setState(state => ({
      ...state,
      customizedFieldsByIdMap: {
        ...state.customizedFieldsByIdMap,
        [subcampaignId]: {
          ...(state.customizedFieldsByIdMap[subcampaignId] || null),
          // $FlowIgnore - Cannot use fieldName [1] as a computed property. Computed properties may only be primitive literal values
          [fieldName]: value
        }
      },
      isModified: true
    }));
  };

  handleBudgetChanged = (event: TextEntryKeyboardEvent): void => {
    const target = event.currentTarget;
    const subcampaignId = target.dataset.subcampaignId;
    const value = target.value;
    this.handleChanged(Number(subcampaignId), target.value ? value : null);
  };

  handlePersonalMessageChanged = (event: TextEntryKeyboardEvent): void => {
    const target = event.currentTarget;
    const subcampaignId = target.dataset.subcampaignId;
    this.handleChanged(Number(subcampaignId), target.value || '');
  };

  handleEditToggle = (): void => {
    this.setState(state => ({
      isEditMode: !state.isEditMode
    }));
  };

  handleSubmit = () => {
    const { fieldName, onCloseModal, onChanged, dispatchSaveCurrentCustomizedFields } = this.props;
    const { customizedFieldsByIdMap } = this.state;

    if (fieldName === 'budget') {
      // expect fields to be validated and map them from string to number
      const mappedCustomizedFields = mapValues(customizedFieldsByIdMap, (mapValue) => {
        if (!isNil(mapValue.budget)) {
          return {
            ...mapValue,
            budget: Number(mapValue.budget)
          };
        }

        return mapValue;
      });

      dispatchSaveCurrentCustomizedFields(mappedCustomizedFields);
    } else {
      dispatchSaveCurrentCustomizedFields(customizedFieldsByIdMap);
    }

    this.dispatchTrackEvent();
    onCloseModal();

    if (onChanged) {
      onChanged(fieldName);
    }
  };

  renderProfile(subcampaign: Subcampaign): React.ReactNode {
    const { influencersMetrics } = this.props;

    return (
      <div className="bulk-customize-offer-modal__cell bulk-customize-offer-modal__cell--profile">
        <CreatorInfoWithChannels
          creatorProfile={subcampaign.creatorProfile}
          includeRatio
          hasInfluencerName
          metrics={influencersMetrics[subcampaign.creatorProfile.id]}
        />
      </div>
    );
  }

  renderRowForBudget(subcampaign: Subcampaign): React.ReactNode {
    const { customizedFieldsByIdMap, isEditMode } = this.state;
    const budget = customizedFieldsByIdMap[subcampaign.id].budget;
    const isIntegerBudgetValidation = isIntegerMoneySum(budget);

    return (
      <div
        key={`bulk-customize-offer-subcampaign-${subcampaign.id}`}
        className="bulk-customize-offer-modal__row"
      >
        {this.renderProfile(subcampaign)}
        <div className="bulk-customize-offer-modal__cell">
          <FormField
            showValidationIcon={false}
            direction="column"
            fontSize="small"
            validators={[
              isRequired(budget),
              isPositiveNumber(budget),
              isIntegerMoneySum(budget)
            ]}
            immediateValidationError={(isIntegerBudgetValidation.valid) ? '' : isIntegerBudgetValidation.message}
          >
            <Input
              hint="€"
              hintAlign="right"
              width="responsive"
              value={!budget ? '' : String(budget)}
              isEditable={isEditMode}
              className="bulk-customize-offer-modal__input"
              data={{ 'subcampaign-id': subcampaign.id }}
              onChange={this.handleBudgetChanged}
            />
          </FormField>
        </div>
        <div className="bulk-customize-offer-modal__cell bulk-customize-offer-modal__cell--comment">
          <CreatorTilePriceReco
            caption={__t('bulk_customize_offer_modal.price_recommendation1')}
            channels={['instagram', 'tiktok']}
            channelsData={subcampaign.creatorProfile.channels}
          />
        </div>
      </div>
    );
  }

  renderRowForLegalRequirements(subcampaign: Subcampaign): React.ReactNode {
    const { customizedFieldsByIdMap, isEditMode } = this.state;
    const legalRequirements = customizedFieldsByIdMap[subcampaign.id].legalRequirements;
    const hasLegalRequirementsFilledForAnyone = Object.values(customizedFieldsByIdMap)
      .map(entry => entry.legalRequirements)
      .filter(isDefined)
      .length > 0;

    return (
      <div
        key={`bulk-customize-offer-subcampaign-${subcampaign.id}`}
        className="bulk-customize-offer-modal__row"
      >
        {this.renderProfile(subcampaign)}
        <div className="bulk-customize-offer-modal__cell bulk-customize-offer-modal__cell--wide">
          <FormField
            showValidationIcon={false}
            direction="column"
            fontSize="small"
            validators={[
              maxLength(legalRequirements, LONG_STRING_MAX_LENGTH),
              isNotEmpty(hasLegalRequirementsFilledForAnyone, __t('bulk_customize_offer_modal.legal_requirements_empty_error'))
            ]}
          >
            <RichTextEditor
              canEdit={isEditMode}
              liveUpdate
              value={legalRequirements}
              onChange={value => this.handleChanged(subcampaign.id, value)}
              placeholder={__t('bulk_customize_offer_modal.legal_requirements_placeholder')}
            />
          </FormField>
        </div>
      </div>
    );
  }

  renderRowForPersonalMessage(subcampaign: Subcampaign): React.ReactNode {
    const { customizedFieldsByIdMap, isEditMode } = this.state;
    const personalMessage = customizedFieldsByIdMap[subcampaign.id].personalMessage;
    const hasPersonalMessageFilledForAnyone = Object.values(customizedFieldsByIdMap)
      .map(entry => entry.personalMessage)
      .filter(isDefined)
      .length > 0;

    return (
      <div
        key={`bulk-customize-offer-subcampaign-${subcampaign.id}`}
        className="bulk-customize-offer-modal__row"
      >
        {this.renderProfile(subcampaign)}
        <div className="bulk-customize-offer-modal__cell bulk-customize-offer-modal__cell--wide">
          <FormField
            showValidationIcon={false}
            direction="column"
            fontSize="small"
            validators={[
              maxLength(personalMessage, LONG_STRING_MAX_LENGTH),
              isNotEmpty(hasPersonalMessageFilledForAnyone, __t('bulk_customize_offer_modal.personal_message_empty_error'))
            ]}
          >
            <Input
              multiline
              value={personalMessage || ''}
              width="responsive"
              isEditable={isEditMode}
              data={{ 'subcampaign-id': subcampaign.id }}
              onChange={this.handlePersonalMessageChanged}
              placeholder={__t('bulk_customize_offer_modal.personal_message_placeholder')}
            />
          </FormField>
        </div>
      </div>
    );
  }

  renderRow(subcampaign: Subcampaign): React.ReactNode {
    const { fieldName } = this.props;
    switch (fieldName) {
      case 'budget':
        return this.renderRowForBudget(subcampaign);
      case 'legalRequirements':
        return this.renderRowForLegalRequirements(subcampaign);
      case 'personalMessage':
        return this.renderRowForPersonalMessage(subcampaign);
      default:
        break;
    }

    return null;
  }

  render() {
    const { fieldName, bulkOfferStore, campaignBrief, onCloseModal } = this.props;
    const { isLoading } = bulkOfferStore;
    const { isModified, selectedSubcampaigns, isEditMode } = this.state;
    const plannedBudget = get(campaignBrief, 'budget');

    return (
      <ModalFrame
        title={__t(`bulk_customize_offer_modal.title_${snakeCase(fieldName)}`)}
        subtitle={!isNil(campaignBrief) && !isEmpty(campaignBrief) ? `"${campaignBrief.name}"` : ''}
      >
        {
          isModified &&
            <PromptExit type="modal" />
        }
        {
          !isNil(plannedBudget) && plannedBudget >= 0 &&
            <ModalFrameMessage
              message={__t('customize_bulk_offer.modal.campaign_desc', {
                totalBudget: renderPrice(plannedBudget || 0),
                remainingBudget: renderPrice(this.calcRemainingBudget())
              })}
            />
        }
        <ModalFrameInner>
          <Form
            ref={this.formRef}
            onSubmit={this.handleSubmit}
          >
            {sortParticipants(selectedSubcampaigns)
              .map(subcampaign => this.renderRow(subcampaign))}
            <ModalFrameFooter>
              <ActionButton
                color="tertiary"
                onClick={onCloseModal}
              >
                {__t('collabary_common.action_button.cancel')}
              </ActionButton>
              {
                !isEditMode && (
                  <ActionButton
                    color="secondary"
                    disabled={isLoading}
                    onClick={this.handleEditToggle}
                  >
                    {__t('collabary_common.action_button.edit')}
                  </ActionButton>
                )
              }
              {
                isEditMode && (
                  <ActionButton
                    color="primary"
                    disabled={isLoading}
                    onClick={(event) => this.formRef.current && this.formRef.current.handleSubmit(event)}
                  >
                    {__t('collabary_common.action_button.save')}
                  </ActionButton>
                )
              }
            </ModalFrameFooter>
          </Form>
        </ModalFrameInner>
      </ModalFrame>
    );
  }
}

const mapStateToProps = ({ bulkOffer, campaignOverview, campaignBrief, influencersMetrics }): StateProps => ({
  bulkOfferStore: bulkOffer,
  campaignOverview: campaignOverview.overview,
  campaignBrief: campaignBrief.brief,
  influencersMetrics: influencersMetrics.data
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchInfluncerMetrics: (campaignId: number | undefined, influencerIds: Array<number>) => dispatch(fetchInfluncerMetrics(campaignId, influencerIds, true)),
  dispatchSaveCurrentCustomizedFields: (customizedFieldsByIdMap) => dispatch(saveCurrentCustomizedFields(customizedFieldsByIdMap))
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkCustomizeOfferModal);
